import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Select,
  Button,
  Stack,
  Flex,
  TextInput,
  Text,
  Alert,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { primaryColor } from '../../../constants/theme';
import { IconChevronDown, IconInfoCircle } from '@tabler/icons-react';

export const BirthDateModalPicker = ({
  value,
  onChange,
  error,
  label,
  placeholder,
  rightSection,
  radius,
  size,
  styles,
  ...others
}: any) => {
  const [opened, { open, close }] = useDisclosure(false);
  const isMobile = useMediaQuery('(max-width: 50em)');
  
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  // Sync value to modal state when opened
  useEffect(() => {
    if (opened) {
      if (value) {
        let d: Date;
        if (typeof value === 'string') {
          const parts = value.split('-');
          if (parts.length === 3) {
            d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          } else {
            d = new Date(value);
          }
        } else {
          d = value;
        }
        
        if (d && !isNaN(d.getTime())) {
          setSelectedMonth((d.getMonth() + 1).toString());
          setSelectedDay(d.getDate().toString());
          setSelectedYear(d.getFullYear().toString());
        } else {
          setSelectedMonth(null);
          setSelectedDay(null);
          setSelectedYear(null);
        }
      } else {
        setSelectedMonth(null);
        setSelectedDay(null);
        setSelectedYear(null);
      }
    }
  }, [value, opened]);

  const today = new Date();
  const currentYear = today.getFullYear();

  const years = useMemo(() => {
    const arr = [];
    for (let i = currentYear; i >= currentYear - 100; i--) {
      arr.push(i.toString());
    }
    return arr;
  }, [currentYear]);

  const months = useMemo(() => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return Array.from({ length: 12 }, (_, i) => ({
      value: (i + 1).toString(),
      label: monthNames[i],
    }));
  }, []);

  const days = useMemo(() => {
    return Array.from({ length: 31 }, (_, i) => (i + 1).toString());
  }, []);

  const isValidDate = useMemo(() => {
    if (!selectedMonth || !selectedDay || !selectedYear) return true; // not fully selected, don't show error yet
    
    // Strip time from today for accurate future date check
    const now = new Date();
    const todayOnlyDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const y = parseInt(selectedYear);
    const m = parseInt(selectedMonth) - 1;
    const d = parseInt(selectedDay);
    
    const assembledDate = new Date(y, m, d);
    
    // Check if legit date (e.g. not Feb 30)
    if (assembledDate.getFullYear() !== y || assembledDate.getMonth() !== m || assembledDate.getDate() !== d) {
      return false;
    }
    
    // Check if not in future
    if (assembledDate > todayOnlyDate) {
      return false;
    }
    
    return true;
  }, [selectedMonth, selectedDay, selectedYear]);

  const handleApply = () => {
    if (selectedMonth && selectedDay && selectedYear && isValidDate) {
      const monthStr = selectedMonth.padStart(2, '0');
      const dayStr = selectedDay.padStart(2, '0');
      const dateString = `${selectedYear}-${monthStr}-${dayStr}`;
      onChange(dateString);
      close();
    }
  };

  let formattedValue = '';
  if (value) {
    let d: Date;
    if (typeof value === 'string') {
      const parts = value.split('-');
      if (parts.length === 3) {
        d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        d = new Date(value);
      }
    } else {
      d = value;
    }
    
    if (d && !isNaN(d.getTime())) {
      formattedValue = d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  return (
    <>
      <div onClick={open} style={{ cursor: 'pointer' }}>
        <TextInput
          radius={radius}
          size={size}
          styles={styles}
          label={label}
          placeholder={placeholder}
          value={formattedValue}
          error={error}
          readOnly
          rightSection={rightSection}
          pointer
          {...others}
        />
      </div>

      <Modal 
        opened={opened} 
        onClose={close} 
        withCloseButton={false}
        centered
        radius="md"
        padding="xl"
        size={isMobile ? '100%' : 'md'}
        trapFocus={false}
        styles={{
          content: { overflow: 'visible' },
          body: { overflow: 'visible' }
        }}
      >
        <Stack align="center" gap="sm">
          
          <Text size="xl" fw={700}>Select Date</Text>
          <Text size="sm" c="dimmed" mb="md" ta="center">Select your birth date using the menus below</Text>

          <Flex direction={{ base: 'column', sm: 'row' }} w="100%" gap="sm" align={{ base: 'stretch', sm: 'flex-end' }}>
            <Select
              style={{ flex: 1 }}
              label={<Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>Month</Text>}
              placeholder="Month"
              data={months}
              value={selectedMonth}
              onChange={setSelectedMonth}
              radius="md"
              size={isMobile ? 'lg' : 'md'}
              maxDropdownHeight={isMobile ? 350 : 250}
              comboboxProps={{ withinPortal: false }}
              rightSection={<IconChevronDown size={14} />}
              styles={{ input: { backgroundColor: '#f4f6f5', border: 'none' } }}
            />
            <Select
              style={{ flex: 1 }}
              label={<Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>Day</Text>}
              placeholder="Day"
              data={days}
              value={selectedDay}
              onChange={setSelectedDay}
              radius="md"
              size={isMobile ? 'lg' : 'md'}
              disabled={!selectedMonth}
              maxDropdownHeight={isMobile ? 350 : 250}
              comboboxProps={{ withinPortal: false }}
              rightSection={<IconChevronDown size={14} />}
              styles={{ input: { backgroundColor: '#f4f6f5', border: 'none' } }}
            />
            <Select
              style={{ flex: 1 }}
              label={<Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>Year</Text>}
              placeholder="Year"
              data={years}
              value={selectedYear}
              onChange={setSelectedYear}
              radius="md"
              size={isMobile ? 'lg' : 'md'}
              maxDropdownHeight={isMobile ? 350 : 250}
              comboboxProps={{ withinPortal: false }}
              rightSection={<IconChevronDown size={14} />}
              styles={{ input: { backgroundColor: '#f4f6f5', border: 'none' } }}
            />
          </Flex>

          {!isValidDate && (
            <Alert color="red" radius={'md'} w="100%" mt="sm" title="Invalid date" icon={<IconInfoCircle />}>
              Please enter a new date
            </Alert>
          )}

          <Button 
            mt="md"
            fullWidth 
            size="md" 
            radius="md"
            color={primaryColor}
            onClick={handleApply}
            disabled={!selectedMonth || !selectedDay || !selectedYear || !isValidDate}
          >
            Apply
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
